import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { widthExpandAnimation } from '@app/animations/width-expand.animation';
import { SortDirection } from '@app/classes';
import { SearchComponent } from '@app/components';
import { AnalyticEvents } from '@app/enums';
import { DocumentFoldersService } from '@app/modules/documents-v2/services/document-folders.service';
import { AnalyticService, AuthService, FileHelperService, NotifyService } from '@app/services';
import { breakPoints } from '@app/styles/theme';
import { DriveTypeDisplayOptions } from '@app/types/translatables/drive-name-display.options';
import { DocumentsV2Resolver } from '@documents-v2/documents-v2.resolver';
import { DriveTypeEnum, SystemFolderNamesEnum } from '@documents-v2/enums';
import { AllDocumentNodesIndexTable, EmployeeDocumentNodesIndexTable } from '@documents-v2/services';
import { DocumentFoldersSidebarComponent } from '@documents-v2/standalone-components';
import { Drive } from '@models/documents/directory/drive.model';
import { Node } from '@models/documents/directory/node.model';
import { EmployeeDocument } from '@models/documents/employee-document.model';
import { MenuColumnEvent } from '@table/interfaces/menu-column-event.interface';
import { Subscription } from 'rxjs';

/**
 * As employee, go to Documents, then you are here.
 */
@Component({
    templateUrl: './my-documents-folders-index.template.html',
    styleUrls: [
        './my-documents-folders-index.style.scss',
        '../../../../../../styles/shared-views/documents-index.scss',
    ],
    animations: [widthExpandAnimation(350)],
})
export class MyDocumentsFoldersIndexView implements OnInit, OnDestroy {
    @ViewChild('sidebarComponent', { static: true }) sidebarComponent!: DocumentFoldersSidebarComponent;
    @ViewChild('searchBar') searchBar!: SearchComponent;

    isLoading = true;
    isTableLoading = false;
    isSearchTableLoading = false;

    pathRoot = 'documents-v2';

    sidebarDrives: Drive[] = [];

    tableParentNode: Node | undefined;
    parentDrive: Drive | undefined;

    driveTypeEnum = DriveTypeEnum;
    driveTypeDisplay = DriveTypeDisplayOptions;

    searchQuery: string = '';
    tableBasedOnSearchQuery: AllDocumentNodesIndexTable | undefined;

    inRecentDocumentsState = false;

    // Incomplete documents include those that have not been acknowledged, not signed, or are missing other required actions
    incompleteDocumentCount = 0;

    protected breadcrumb: { node: Node | null; name: string }[] = [];

    protected resolver: DocumentsV2Resolver;

    protected subscription: Subscription | undefined;

    private _inSearchState: boolean = false;

    constructor(
        private analyticsService: AnalyticService,
        private breakpointObserver: BreakpointObserver,
        private documentFoldersService: DocumentFoldersService,
        private fileHelper: FileHelperService,
        private injector: Injector,
        private notify: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
        protected auth: AuthService,
        public table: EmployeeDocumentNodesIndexTable
    ) {
        this.resolver = this.injector.get(DocumentsV2Resolver);
        this.table.onLoading.subscribe((isLoading) => {
            this.isTableLoading = isLoading;
        });
    }

    get inSearchState(): boolean {
        return this._inSearchState;
    }

    set inSearchState(value: boolean) {
        if (value && !this.tableBasedOnSearchQuery) {
            this.loadTableBasedOnAllDrives();
        }
        this._inSearchState = value;
    }

    get isMobile(): boolean {
        return !this.breakpointObserver.isMatched(`(min-width: ${breakPoints.lg}px)`);
    }

    ngOnInit(): void {
        this.setSidebarDrives().then(() => {
            this.subscription = this.route.params.subscribe(() => this.resolveNode());
        });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    onClick(node: Node): void {
        if (node.isEmployeeDocument() && this.tableParentNode) {
            this.router.navigate(
                ['/' + this.pathRoot, 'employees', this.auth.employee.id, 'employee-documents', node.source.id],
                {
                    queryParams: { folderId: this.tableParentNode.id },
                }
            );
            return;
        }
        if (node.isCompanyDocument() && this.tableParentNode) {
            this.router.navigate(['/' + this.pathRoot, 'center', node.sourceId], {
                queryParams: { folderId: this.tableParentNode.id },
            });
            return;
        }
        this.load(node);
    }

    async menuAction(event: MenuColumnEvent): Promise<void> {
        const { menuItem, data } = event;
        const node = data as Node;
        const document = node.source;

        if (menuItem.event !== 'download') {
            return;
        }

        EmployeeDocument.param('employee', document.employeeId)
            .find(document.id)
            .then((employeeDocument: EmployeeDocument) => this.onDownload(employeeDocument))
            .catch((err) => this.notify.error(err));
    }

    /**
     * Resolve is handled within view (not in Routes), this allows us to subscribe to changes in the path and to show
     * the next folder when clicked (Resolvers are not re-resolved on same view).
     */
    resolveNode(): void {
        this.isLoading = true;

        this.resolver
            .resolve(this.route.snapshot, this.router.routerState.snapshot)
            .then((models) => {
                const node = <Node>models.get('node');
                if (node) {
                    this.load(node);
                } else {
                    this.load(this.tableParentNode ?? null);
                }
            })
            .catch((err) => err.message || 'document.unableToReloadTable');
    }

    async load(node: Node | null): Promise<void> {
        this.inSearchState = false;
        this.inRecentDocumentsState = false;

        if (node) {
            this.isLoading = true;
            this.tableParentNode = node;
            await this.setParentDrive(this.tableParentNode);

            this.table.loadNode(this.tableParentNode, this.parentDrive);
            this.expandParentNode();
            await this.loadBreadcrumb(node.id);
            this.isLoading = false;
        }
    }

    onAddEmployeeDocument(): void {
        this.router.navigate(['/profile', this.pathRoot, 'bulk-upload']);
    }

    onSearch(searchQuery: string): void {
        if (!searchQuery) {
            this.inSearchState = false;
            return;
        } else {
            this.inSearchState = true;
            this.inRecentDocumentsState = false;
            this.analyticsService.trackEvent(AnalyticEvents.FiltersDocumentsAndFoldersViaSearchBar);
        }

        this.searchQuery = searchQuery;
        if (this.tableBasedOnSearchQuery) {
            this.tableBasedOnSearchQuery.paginator.search = searchQuery;
        }
    }

    onClearSearchFilters(): void {
        this.inSearchState = false;
        this.inRecentDocumentsState = false;
        this.searchQuery = '';
        this.tableBasedOnSearchQuery?.clearFilters();

        if (this.searchBar) {
            this.searchBar.clear();
        }
    }

    onRecentDocuments(): void {
        this.onClearSearchFilters();

        this.inSearchState = true;
        this.inRecentDocumentsState = true;

        if (this.tableBasedOnSearchQuery) {
            this.tableBasedOnSearchQuery.paginator.setSort({ column: 'updatedAt', order: SortDirection.DESC });
            this.tableBasedOnSearchQuery.paginator.search = '%';
        }
    }

    /**
     * Incomplete documents can only be selected from the root of the employee drive
     */
    async onIncompleteDocument(): Promise<void> {
        if (this.parentDrive?.type !== DriveTypeEnum.employee || this.table.node !== this.parentDrive?.node) {
            const targetDrive = this.sidebarDrives.find((drive) => drive.isEmployee());
            if (targetDrive) {
                this.table.loadNode(targetDrive.node, targetDrive);
                this.loadBreadcrumb(targetDrive.node.id);
            }
        }

        this.onClearSearchFilters();

        if (this.table?.paginator) {
            this.table.paginator.filters = { incomplete: true, recursive: true };
        }
    }

    isIncompleteView(): boolean {
        if (this.inSearchState || this.inRecentDocumentsState) {
            return false;
        }

        if (this.table?.paginator) {
            return this.table.paginator.filters.incomplete === true;
        }

        return false;
    }

    maybeSystemFolder(x: string): string {
        const entries = Object.entries(SystemFolderNamesEnum) as [keyof typeof SystemFolderNamesEnum, string][];
        for (const [key, value] of entries) {
            if (value === x) {
                return `document.systemFolders.${key}`;
            }
        }
        return x;
    }

    private onDownload(employeeDocument: EmployeeDocument): void {
        this.fileHelper.save(employeeDocument.file.id, employeeDocument.name, employeeDocument.file.type ?? '');
    }

    /**
     * Load the drives this user can access
     */
    private async setSidebarDrives(): Promise<void> {
        try {
            const [drives] = await Drive.all();
            this.sidebarDrives = drives.filter((drive: Drive) => drive.isEmployee() || drive.isPublic());
            this.defaultTableParentNode();

            // Initialize incomplete documents count
            const firstEmployeeDrive = drives.find((drive: Drive) => drive.isEmployee());
            if (firstEmployeeDrive) {
                this.getIncompleteDocumentCount(firstEmployeeDrive).then((count) => {
                    this.incompleteDocumentCount = count;
                });
            }
        } catch (err) {
            const errorMessage = this.isErrorWithMessage(err) ? err.message : 'document.couldNotFetchSidebarDrives';
            this.notify.error(errorMessage);
        }
    }

    private defaultTableParentNode(): void {
        if (!this.tableParentNode) {
            const drive = this.getDefaultDrive();
            this.tableParentNode = drive?.node;
        }
    }

    private getDefaultDrive(): Drive | null {
        let drive = this.sidebarDrives.find((sidebarDrive: Drive) => sidebarDrive.isEmployee());
        if (drive) {
            return drive;
        }

        drive = this.sidebarDrives.find((sidebarDrive: Drive) => sidebarDrive.isPublic());
        if (drive) {
            return drive;
        }

        return null;
    }

    private expandParentNode(): void {
        if (this.tableParentNode?.parentNodeId) {
            this.sidebarComponent.expandParentNode(this.tableParentNode.parentNodeId);
        }
    }

    private async setParentDrive(tableParentNode: Node): Promise<void> {
        try {
            this.parentDrive = await this.documentFoldersService.getDriveBasedOnNode(tableParentNode);
        } catch (err) {
            const errorMessage = this.isErrorWithMessage(err) ? err.message : 'document.couldNotFetchParentDrive';
            this.notify.error(errorMessage);
        }
    }

    /**
     * This table is used to display contents based on search query
     */
    private loadTableBasedOnAllDrives(): void {
        this.tableBasedOnSearchQuery = new AllDocumentNodesIndexTable(
            this.auth,
            this.sidebarDrives.map((drive) => drive.node.id)
        );

        // Disable features employee can not do
        this.tableBasedOnSearchQuery.bulkSelect = false;
        this.tableBasedOnSearchQuery.allowDragAndDrop = false;
        this.tableBasedOnSearchQuery.showGrippyOnCheckbox = false;
        this.tableBasedOnSearchQuery.showMenuColumnOptions = false;

        this.tableBasedOnSearchQuery.onLoading.subscribe((isLoading) => {
            this.isSearchTableLoading = isLoading;
        });
    }

    private async getIncompleteDocumentCount(drive: Drive): Promise<number> {
        const [, meta] = await Node.whereIn('node', [drive.node.id])
            .where('recursive', true)
            .where('incomplete', true)
            .limit(1)
            .page(1)
            .get();
        return meta.pagination.total;
    }

    private async loadBreadcrumb(nodeId: number): Promise<void> {
        const ancestors = await this.documentFoldersService.getNodeAncestors(nodeId);
        this.breadcrumb = ancestors.map((node) => ({ node: node, name: node.name }));
        // remove the drive node to override with display name
        const driveCrumb = this.breadcrumb.shift();

        const driveDisplayName =
            this.parentDrive?.type === this.driveTypeEnum.public
                ? this.driveTypeDisplay['public']
                : this.driveTypeDisplay['employee'];
        this.breadcrumb.unshift(
            { name: 'document.allDocuments', node: null },
            { name: driveDisplayName, node: driveCrumb?.node ?? null }
        );
    }

    /**
     * Provides flexibility if you're dealing with different types of errors
     */
    private isErrorWithMessage(err: unknown): err is Error {
        return typeof err === 'object' && err !== null && 'message' in err;
    }
}
